import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import ProductCard from "./ProductCard";
import QuickView from "./QuickView";
import { fetchProducts, fetchCategories } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Skeleton } from "@/components/ui/skeleton";

export default function ProductGrid() {
  const { t } = useI18n();
  const [category, setCategory] = useState("all");
  const [quickProduct, setQuickProduct] = useState(null);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", category],
    queryFn: () => fetchProducts(category),
  });

  const catMap = Object.fromEntries(categories.map((c) => [c.slug, c.name]));
  const filters = [{ slug: "all", name: t("categories.all") }, ...categories];

  return (
    <section id="products" className="mx-auto max-w-[1400px] scroll-mt-24 px-5 py-24 md:px-10 md:py-32">
      <div className="mb-12 flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <div>
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-primary"
          >
            <span className="h-px w-8 bg-primary" /> {t("nav.products")}
          </motion.span>
          <h2 className="font-display text-4xl font-bold uppercase leading-none tracking-tight md:text-6xl">
            {t("products.title")}
          </h2>
          <p className="mt-4 max-w-md text-muted-foreground">{t("products.subtitle")}</p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="category-filter">
          {filters.map((cat) => (
            <button
              key={cat.slug}
              data-testid={`filter-${cat.slug}`}
              onClick={() => setCategory(cat.slug)}
              className={`border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                category === cat.slug
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-4">
              <Skeleton className="aspect-[3/4] w-full" />
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="py-20 text-center text-muted-foreground">{t("products.empty")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onQuickView={setQuickProduct} categoryName={catMap[p.category]} />
          ))}
        </div>
      )}

      <QuickView product={quickProduct} categoryName={quickProduct ? catMap[quickProduct.category] : ""} onClose={() => setQuickProduct(null)} />
    </section>
  );
}
